import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Edit, Save, X, GitBranch, Calendar, User, Eye, RotateCcw, GitPullRequest, Users, GitFork, ChevronDown, ScatterChart, Settings, Flask, BarChart3, Plus, Trash2 } from 'lucide-react';
import Editor from '@monaco-editor/react';
import { getPrompt, createPromptVersion, activatePromptVersion, getBranches, createBranch, updatePromptVersion, deletePromptVersion, deleteBranch } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import PromptTabs from '../components/PromptTabs';
import VersionContextMenu from '../components/VersionContextMenu';
import VersionDetailsModal from '../components/VersionDetailsModal';
import AmendVersionModal from '../components/AmendVersionModal';
import { useFeedback } from '../contexts/FeedbackContext';

const PromptDetail = ({ user }) => {
  const { promptId } = useParams();
  const { showContextualFeedback } = useFeedback();
  const [prompt, setPrompt] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [changeMessage, setChangeMessage] = useState('');
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const [showCreateBranchModal, setShowCreateBranchModal] = useState(false);
  const [newBranchData, setNewBranchData] = useState({
    name: '',
    description: '',
    baseBranchId: null
  });
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    show: false,
    position: null,
    version: null
  });
  
  // Modal state
  const [showVersionDetails, setShowVersionDetails] = useState(null);
  const [showAmendModal, setShowAmendModal] = useState(null);

  useEffect(() => {
    loadData();
  }, [promptId]);

  const loadData = async (selectBranchId = null) => {
    try {
      const [promptData, branchesData] = await Promise.all([
        getPrompt(promptId),
        getBranches(promptId, user.id)
      ]);
      
      setPrompt(promptData);
      setBranches(branchesData);
      
      let branchToSelect = null;
      
      // If a specific branch ID is provided, try to select it
      if (selectBranchId) {
        branchToSelect = branchesData.find(b => b.id === selectBranchId);
      }
      
      // If no specific branch or it doesn't exist, try to restore the last selected branch from localStorage
      if (!branchToSelect) {
        const lastSelectedBranchKey = `lastSelectedBranch_${promptId}_${user.id}`;
        const lastSelectedBranchId = localStorage.getItem(lastSelectedBranchKey);
        
        if (lastSelectedBranchId) {
          // Try to find the previously selected branch
          branchToSelect = branchesData.find(b => b.id === lastSelectedBranchId);
        }
      }
      
      // If no previously selected branch or it doesn't exist anymore, use default
      if (!branchToSelect) {
        branchToSelect = branchesData.find(b => b.is_default);
      }
      
      if (branchToSelect) {
        setSelectedBranch(branchToSelect);
        
        // Store the selected branch in localStorage
        const lastSelectedBranchKey = `lastSelectedBranch_${promptId}_${user.id}`;
        localStorage.setItem(lastSelectedBranchKey, branchToSelect.id);
        
        // Get versions for the selected branch only
        const branchVersions = promptData.versions.filter(v => v.branch_id === branchToSelect.id);
        const activeVersion = branchVersions.find(v => v.is_active);
        if (activeVersion) {
          setSelectedVersion(activeVersion);
          setEditContent(activeVersion.content);
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBranchChange = (branch) => {
    setSelectedBranch(branch);
    setShowBranchDropdown(false);
    
    // Store the selected branch in localStorage
    const lastSelectedBranchKey = `lastSelectedBranch_${promptId}_${user.id}`;
    localStorage.setItem(lastSelectedBranchKey, branch.id);
    
    // Get versions for the selected branch only
    const branchVersions = prompt.versions.filter(v => v.branch_id === branch.id);
    const activeVersion = branchVersions.find(v => v.is_active);
    if (activeVersion) {
      setSelectedVersion(activeVersion);
      if (!isEditing) {
        setEditContent(activeVersion.content);
      }
    } else if (branchVersions.length > 0) {
      // If no active version, select the latest one
      const latestVersion = branchVersions[0];
      setSelectedVersion(latestVersion);
      if (!isEditing) {
        setEditContent(latestVersion.content);
      }
    } else {
      setSelectedVersion(null);
      if (!isEditing) {
        setEditContent('');
      }
    }
  };

  const handleCreateBranch = async () => {
    if (!newBranchData.name.trim()) return;

    try {
      const branchData = {
        promptId,
        name: newBranchData.name,
        description: newBranchData.description,
        baseBranchId: newBranchData.baseBranchId || selectedBranch?.id,
        userId: user.id
      };

      const response = await createBranch(branchData);
      const newBranchId = response.branchId;
      
      // Reset form and close modal
      setNewBranchData({ name: '', description: '', baseBranchId: null });
      setShowCreateBranchModal(false);
      
      // Reload data and automatically switch to the new branch
      await loadData(newBranchId);

      // Show contextual feedback after creating a branch
      showContextualFeedback({
        actionName: 'Create Branch',
        actionContext: `Created new branch "${newBranchData.name}" for prompt "${prompt?.name}"`,
        position: 'bottom-right'
      });
      
    } catch (error) {
      console.error('Failed to create branch:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleSaveVersion = async () => {
    if (!editContent.trim() || !changeMessage.trim() || !selectedBranch) return;

    try {
      await createPromptVersion(promptId, {
        content: editContent,
        changeMessage,
        authorId: user.id,
        branchId: selectedBranch.id
      });
      
      setIsEditing(false);
      setChangeMessage('');
      loadData();

      // Show contextual feedback after creating a version
      showContextualFeedback({
        actionName: 'Create Version',
        actionContext: `Created new version for prompt "${prompt?.name}" in branch "${selectedBranch.name}"`,
        position: 'bottom-right'
      });
    } catch (error) {
      console.error('Failed to save version:', error);
    }
  };

  const handleActivateVersion = async (versionId) => {
    try {
      await activatePromptVersion(promptId, versionId);
      loadData();

      // Show contextual feedback after activating a version
      showContextualFeedback({
        actionName: 'Activate Version',
        actionContext: `Activated version for prompt "${prompt?.name}"`,
        position: 'bottom-right'
      });
    } catch (error) {
      console.error('Failed to activate version:', error);
    }
  };

  const handleVersionSelect = (version) => {
    setSelectedVersion(version);
    if (!isEditing) {
      setEditContent(version.content);
    }
  };

  // Context menu handlers
  const handleContextMenu = (e, version) => {
    e.preventDefault();
    setContextMenu({
      show: true,
      position: { x: e.clientX, y: e.clientY },
      version
    });
  };

  const handleAmendVersion = (version) => {
    setShowAmendModal(version);
  };

  const handleDeleteVersion = async (version) => {
    if (window.confirm(`Are you sure you want to delete version ${version.version_number}? This action cannot be undone.`)) {
      try {
        await deletePromptVersion(promptId, version.id);
        loadData();
        
        // If the deleted version was selected, select another one
        if (selectedVersion?.id === version.id) {
          const branchVersions = prompt.versions.filter(v => v.branch_id === selectedBranch.id && v.id !== version.id);
          if (branchVersions.length > 0) {
            const newSelected = branchVersions.find(v => v.is_active) || branchVersions[0];
            setSelectedVersion(newSelected);
            setEditContent(newSelected.content);
          } else {
            setSelectedVersion(null);
            setEditContent('');
          }
        }
      } catch (error) {
        console.error('Failed to delete version:', error);
        alert('Failed to delete version. Please try again.');
      }
    }
  };

  const handleCopyContent = async (version) => {
    try {
      await navigator.clipboard.writeText(version.content);
    } catch (error) {
      console.error('Failed to copy content:', error);
    }
  };

  const handleDuplicateVersion = async (version) => {
    if (!selectedBranch) return;
    
    try {
      await createPromptVersion(promptId, {
        content: version.content,
        changeMessage: `Duplicate of version ${version.version_number}`,
        authorId: user.id,
        branchId: selectedBranch.id
      });
      loadData();
    } catch (error) {
      console.error('Failed to duplicate version:', error);
    }
  };

  const handleSetActive = async (version) => {
    try {
      await activatePromptVersion(promptId, version.id);
      loadData();
    } catch (error) {
      console.error('Failed to activate version:', error);
    }
  };

  const handleExportVersion = (version) => {
    const content = version.content;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prompt-v${version.version_number}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleViewDetails = (version) => {
    setShowVersionDetails(version);
  };

  const handleAmendSave = async (versionData) => {
    if (!showAmendModal) return;
    
    try {
      await updatePromptVersion(promptId, showAmendModal.id, versionData);
      setShowAmendModal(null);
      loadData();
      
      // Update the editor content if this is the selected version
      if (selectedVersion?.id === showAmendModal.id) {
        setEditContent(versionData.content);
      }
    } catch (error) {
      console.error('Failed to amend version:', error);
      throw error; // Re-throw to let the modal handle it
    }
  };

  const handleDeleteBranch = async (branch) => {
    if (window.confirm(`Are you sure you want to delete branch "${branch.name}"? This action cannot be undone.`)) {
      try {
        await deleteBranch(branch.id, user.id);
        
        // If the deleted branch was the selected one, switch to default branch
        if (selectedBranch?.id === branch.id) {
          const defaultBranch = branches.find(b => b.is_default);
          if (defaultBranch) {
            await loadData(defaultBranch.id);
          } else {
            await loadData();
          }
        } else {
          await loadData();
        }
      } catch (error) {
        console.error('Failed to delete branch:', error);
        const errorMessage = error.response?.data?.error || 'Failed to delete branch. Please try again.';
        alert(errorMessage);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!prompt) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Prompt not found</h2>
        <Link to="/" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  // Get versions for the selected branch only
  const branchVersions = selectedBranch 
    ? prompt.versions.filter(v => v.branch_id === selectedBranch.id)
    : [];
  const activeVersion = branchVersions.find(v => v.is_active);

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/" className="hover:text-primary-600">Dashboard</Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/projects/${prompt.project_id}`} className="hover:text-primary-600">
          {prompt.project_name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-gray-900">{prompt.name}</span>
      </nav>

      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <h1 className="text-3xl font-bold text-gray-900">{prompt.name}</h1>
            
            {/* Branch Selector */}
            {selectedBranch && (
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setShowBranchDropdown(!showBranchDropdown);
                  }}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <GitBranch className="h-4 w-4 mr-2" />
                  {selectedBranch.name}
                  <ChevronDown className="h-4 w-4 ml-2" />
                </button>
                
                {showBranchDropdown && (
                  <div className="absolute z-10 mt-1 w-64 bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {branches.map((branch) => (
                      <div
                        key={branch.id}
                        className={`flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-100 ${
                          selectedBranch.id === branch.id ? 'bg-primary-50 text-primary-700' : 'text-gray-900'
                        }`}
                      >
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleBranchChange(branch);
                          }}
                          className="flex-1 text-left flex items-center"
                        >
                          <GitBranch className="h-4 w-4 mr-2" />
                          <span className="flex-1">{branch.name}</span>
                          {branch.is_default && (
                            <span className="text-xs text-green-600 ml-2">default</span>
                          )}
                        </button>
                        
                        {/* Delete button for non-default branches */}
                        {!branch.is_default && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowBranchDropdown(false);
                              handleDeleteBranch(branch);
                            }}
                            className="ml-2 p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title={`Delete branch ${branch.name}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))}
                    
                    {/* Create New Branch Option */}
                    <div className="border-t border-gray-200 mt-1 pt-1">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setShowBranchDropdown(false);
                          setShowCreateBranchModal(true);
                          setNewBranchData(prev => ({ ...prev, baseBranchId: selectedBranch?.id }));
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-primary-600 hover:bg-primary-50 flex items-center"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create new branch
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {prompt.description && (
            <p className="text-gray-600 mt-2">{prompt.description}</p>
          )}
          <div className="flex items-center space-x-4 text-sm text-gray-500 mt-2">
            <div className="flex items-center">
              <GitBranch className="h-4 w-4 mr-1" />
              {branchVersions.length} version{branchVersions.length !== 1 ? 's' : ''} in {selectedBranch?.name || 'branch'}
            </div>
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              Created {formatDistanceToNow(new Date(prompt.created_at), { addSuffix: true })}
            </div>
          </div>
        </div>
        
        {!isEditing ? (
          <div className="flex space-x-3">
            <Link
              to={`/prompts/${promptId}/settings`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Link>
            <button
              onClick={() => setIsEditing(true)}
              className="btn-primary"
              disabled={!selectedBranch}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Prompt
            </button>
          </div>
        ) : (
          <div className="flex space-x-3">
            <button
              onClick={handleSaveVersion}
              className="btn-primary"
              disabled={!editContent.trim() || !changeMessage.trim()}
            >
              <Save className="h-4 w-4 mr-2" />
              Save Version
            </button>
            <button
              onClick={() => {
                setIsEditing(false);
                setEditContent(selectedVersion?.content || '');
                setChangeMessage('');
              }}
              className="btn-secondary"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Collaboration Navigation Tabs */}
      <PromptTabs promptId={promptId} />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Editor Area */}
        <div className="lg:col-span-3">
          <div className="card">
            {/* Editor Header */}
            <div className="border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {isEditing ? 'Edit Prompt' : 'Prompt Content'}
                  </h3>
                  {selectedVersion && !isEditing && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      v{selectedVersion.version_number}
                      {selectedVersion.is_active && ' (Active)'}
                    </span>
                  )}
                </div>
                
                {selectedVersion && !isEditing && !selectedVersion.is_active && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleSetActive(selectedVersion);
                    }}
                    className="btn-secondary text-sm"
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Make Active
                  </button>
                )}
              </div>
              
              {isEditing && (
                <div className="mt-4">
                  <label htmlFor="changeMessage" className="block text-sm font-medium text-gray-700 mb-1">
                    Describe your changes
                  </label>
                  <input
                    type="text"
                    id="changeMessage"
                    value={changeMessage}
                    onChange={(e) => setChangeMessage(e.target.value)}
                    className="input"
                    placeholder="e.g., Updated tone to be more professional"
                    required
                  />
                </div>
              )}
            </div>

            {/* Monaco Editor */}
            <div className="p-6">
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <Editor
                  height="500px"
                  defaultLanguage="text"
                  value={editContent}
                  onChange={(value) => setEditContent(value || '')}
                  options={{
                    readOnly: !isEditing,
                    minimap: { enabled: false },
                    lineNumbers: 'on',
                    wordWrap: 'on',
                    scrollBeyondLastLine: false,
                    fontSize: 14,
                    fontFamily: 'ui-monospace, SFMono-Regular, Monaco, Consolas, Liberation Mono, Courier New, monospace',
                    theme: 'vs',
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Version History Sidebar */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="border-b border-gray-200 px-4 py-3">
              <h3 className="text-lg font-semibold text-gray-900">Version History</h3>
            </div>
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {branchVersions.map((version) => (
                <div
                  key={version.id}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedVersion?.id === version.id ? 'bg-primary-50 border-r-2 border-primary-500' : ''
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleVersionSelect(version);
                  }}
                  onContextMenu={(e) => handleContextMenu(e, version)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-900">
                      v{version.version_number}
                    </span>
                    {version.is_active && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    )}
                  </div>
                  
                  {version.change_message && (
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                      {version.change_message}
                    </p>
                  )}
                  
                  <div className="flex items-center text-xs text-gray-500">
                    <User className="h-3 w-3 mr-1" />
                    <span className="mr-2">{version.author_name || 'Unknown'}</span>
                    <Calendar className="h-3 w-3 mr-1" />
                    <span>{formatDistanceToNow(new Date(version.created_at), { addSuffix: true })}</span>
                  </div>
                  
                  {selectedVersion && selectedVersion.id !== version.id && (
                    <div className="mt-2">
                      <Link
                        to={`/prompts/${promptId}/diff/${version.version_number}/${selectedVersion.version_number}`}
                        className="text-xs text-primary-600 hover:text-primary-700"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Eye className="h-3 w-3 inline mr-1" />
                        Compare with v{selectedVersion.version_number}
                      </Link>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Create Branch Modal */}
      {showCreateBranchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Create New Branch
            </h3>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="branchName" className="block text-sm font-medium text-gray-700 mb-1">
                  Branch Name *
                </label>
                <input
                  type="text"
                  id="branchName"
                  value={newBranchData.name}
                  onChange={(e) => setNewBranchData({ ...newBranchData, name: e.target.value })}
                  className="input"
                  placeholder="e.g., feature/improve-tone"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="branchDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="branchDescription"
                  value={newBranchData.description}
                  onChange={(e) => setNewBranchData({ ...newBranchData, description: e.target.value })}
                  className="input"
                  rows={3}
                  placeholder="Describe what this branch is for..."
                />
              </div>
              
              <div>
                <label htmlFor="baseBranch" className="block text-sm font-medium text-gray-700 mb-1">
                  Base Branch
                </label>
                <select
                  id="baseBranch"
                  value={newBranchData.baseBranchId || ''}
                  onChange={(e) => setNewBranchData({ ...newBranchData, baseBranchId: e.target.value || null })}
                  className="input"
                >
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name} {branch.is_default ? '(default)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  The new branch will be created from the latest version of the selected base branch.
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleCreateBranch}
                className="btn-primary"
                disabled={!newBranchData.name.trim()}
              >
                Create Branch
              </button>
              <button
                onClick={() => {
                  setShowCreateBranchModal(false);
                  setNewBranchData({ name: '', description: '', baseBranchId: null });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu.show && (
        <VersionContextMenu
          position={contextMenu.position}
          version={contextMenu.version}
          onClose={() => setContextMenu({ show: false, position: null, version: null })}
          onAmend={handleAmendVersion}
          onDelete={handleDeleteVersion}
          onCopyContent={handleCopyContent}
          onDuplicate={handleDuplicateVersion}
          onSetActive={handleSetActive}
          onExport={handleExportVersion}
          onViewDetails={handleViewDetails}
          isActive={contextMenu.version?.is_active}
        />
      )}

      {/* Version Details Modal */}
      {showVersionDetails && (
        <VersionDetailsModal
          version={showVersionDetails}
          onClose={() => setShowVersionDetails(null)}
        />
      )}

      {/* Amend Version Modal */}
      {showAmendModal && (
        <AmendVersionModal
          version={showAmendModal}
          onClose={() => setShowAmendModal(null)}
          onSave={handleAmendSave}
        />
      )}
    </div>
  );
};

export default PromptDetail; 